/**
 * Navbar — glass pill container primitive (bottom-nav shell).
 *
 * Figma source: `navbar` component-set `248:6316`. Inspected variant:
 * state=home (nodeId `248:6315`). See design-system-manifest §5.4.
 *
 * Geometry (all from manifest):
 *  - Root: layoutMode=VERTICAL, pad T/B 4 L/R 0, cornerRadius 48,
 *    fill=glass-16 (surface-4), backdrop-blur=80px, strokeAlign INSIDE.
 *  - Stroke: GRADIENT_LINEAR handles (1,1)→(0,0) with stops
 *    rgba(102,102,102,0) → rgba(102,102,102,0.48). strokeWeight 1.
 *    Rendered as a pseudo-border overlay with mask-based ring technique.
 *  - Inner container: HORIZONTAL, L/R 16 padding, gap 8, H=48 (center/center).
 *
 * Children: caller composes `MenuItem | UIButton | MenuItem` (state=home).
 * This primitive is state-agnostic — it's just the shell. Callers pick
 * what goes inside (navbar has 3 state variants: default / home / library,
 * they differ only in the children, not the shell geometry).
 *
 * Accessibility: the caller is responsible for <nav role> if semantics are
 * needed; this primitive is a visual shell (`div`). BottomNav.jsx will wrap
 * it with <nav aria-label>.
 *
 * Props:
 *  - children (nodes to place inside)
 *  - className? passthrough
 *  - innerClassName? passthrough for the inner layout row
 */
export default function Navbar({ children, className = '', innerClassName = '', ...rest }) {
  const rootCls = [
    'relative w-full',
    // Figma vertical padding only (outer pad T/B=4, L/R=0). inner container
    // handles L/R=16 via px-4. Using py-1 keeps a 4px breathing-space above
    // and below the inner 48 — per navbar.json state=default geometry.
    'py-1',
    'rounded-full',
    // Glass-16 fill + backdrop-blur 80
    'bg-glass-16 backdrop-blur-navbar',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const innerCls = innerClassName
    ? ['relative flex items-center justify-center', innerClassName].join(' ')
    : 'relative flex items-center justify-center gap-2 px-4 h-12';

  return (
    <div className={rootCls} {...rest}>
      {/*
        glass-stroke gradient pseudo-border.
        Figma stroke = GRADIENT_LINEAR (1,1)→(0,0) — bottom-right→top-left —
        stops rgba(102,102,102,0) → rgba(102,102,102,0.48). We emulate via
        an absolute overlay + CSS `mask` ring technique (1px thickness).
        background-linear-gradient `to top left` matches Figma's (1,1)→(0,0).

        The inline `style` here uses non-Figma CSS mechanics (mask-composite),
        NOT a raw hex — stroke stops come straight from design-system-manifest
        §4 `glass-stroke`. This is the only Tailwind-arbitrary-free path to
        reproduce a gradient border in Tailwind 3.
      */}
      <span
        data-navbar-stroke
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          padding: '1px',
          background:
            'linear-gradient(to top left, rgba(102,102,102,0.48), rgba(102,102,102,0))',
          WebkitMask:
            'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          maskComposite: 'exclude',
        }}
      />
      <div
        data-navbar-inner
        className={innerCls}
      >
        {children}
      </div>
    </div>
  );
}
